import {
  ChevronDownIcon,
  DollarSignIcon,
  PercentIcon,
  ReceiptTextIcon,
  SplitIcon,
  UsersIcon,
} from 'lucide-react';

import React from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import { SplitMethod } from '@splitfiesta/shared';
import { Button } from '@splitfiesta/ui';

import { CheckmarkIcon } from '@/components/ui/CheckmarkIcon';

interface SplitTypeInfo {
  value: SplitMethod;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface SplitTypeSelectorProps {
  splitMethod: SplitMethod;
  setSplitMethod: (method: SplitMethod) => void;
  showDropdown: boolean;
  setShowDropdown: (show: boolean) => void;
  isLoading?: boolean;
  /** "Split by item": not a `SplitMethod` (it is stored as custom), so it is passed in. */
  itemsOption?: {
    label: string;
    selected: boolean;
    onSelect: () => void;
  };
}

export function SplitTypeSelector({
  splitMethod,
  setSplitMethod,
  showDropdown,
  setShowDropdown,
  isLoading = false,
  itemsOption,
}: SplitTypeSelectorProps) {
  const { t } = useTranslation();

  const splitMethods: SplitTypeInfo[] = [
    {
      value: 'equal',
      label: t('expenses:splitMethods.equal'),
      icon: SplitIcon,
    },
    {
      value: 'percentage',
      label: t('expenses:splitMethods.percentage'),
      icon: PercentIcon,
    },
    {
      value: 'shares',
      label: t('expenses:splitMethods.shares'),
      icon: UsersIcon,
    },
    {
      value: 'custom',
      label: t('expenses:splitMethods.custom'),
      icon: DollarSignIcon,
    },
  ];

  const itemsSelected = !!itemsOption?.selected;
  const currentMethod = itemsSelected
    ? undefined
    : splitMethods.find(m => m.value === splitMethod);
  const currentMethodLabel = itemsSelected
    ? (itemsOption?.label ?? '')
    : currentMethod?.label || '';
  const CurrentMethodIcon = itemsSelected
    ? ReceiptTextIcon
    : currentMethod?.icon || SplitIcon;

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-3">
      <div className="relative w-full sm:w-auto">
        <Button
          variant="outline"
          size="md"
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={isLoading}
          rightIcon={<ChevronDownIcon className="w-4 h-4" />}
          leftIcon={
            <CurrentMethodIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
          }
          className="w-full sm:w-auto justify-between bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          <span className="font-medium text-left">{currentMethodLabel}</span>
        </Button>

        {showDropdown && (
          <div
            className="absolute z-10 mt-2 w-full sm:w-max bg-white dark:bg-gray-800 rounded-lg shadow-lg ring-1 ring-black/5 dark:ring-gray-700 overflow-hidden"
            data-testid="split-dropdown"
          >
            <div className="py-1 sm:whitespace-nowrap">
              {splitMethods.map(method => (
                <button
                  key={method.value}
                  type="button"
                  data-testid={`split-option-${method.value}`}
                  onClick={() => {
                    setSplitMethod(method.value);
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center pl-2 pr-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  {!itemsSelected && splitMethod === method.value ? (
                    <CheckmarkIcon className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mr-1.5" />
                  ) : (
                    <div className="w-4 h-4 mr-1.5" />
                  )}
                  <method.icon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                  <span className="flex-1">{method.label}</span>
                </button>
              ))}
              {itemsOption ? (
                <button
                  type="button"
                  data-testid="split-option-lineItems"
                  onClick={() => {
                    itemsOption.onSelect();
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center pl-2 pr-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-[#F6F5F3] dark:hover:bg-gray-700/50 transition-colors text-left"
                >
                  {itemsSelected ? (
                    <CheckmarkIcon className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0 mr-1.5" />
                  ) : (
                    <div className="w-4 h-4 mr-1.5" />
                  )}
                  <ReceiptTextIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mr-2" />
                  <span className="flex-1">{itemsOption.label}</span>
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
